// src/components/planning/HistoryDrawer/HistoryDrawer.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronDown, Clock } from 'lucide-react';
import { fetchServiceHistory, ServiceHistoryResponse, HistoryYear, HistorySelection } from '../../../services/historyService';
import { canonicalSlot, slotRank } from '../../../utils/slotNames';
import styles from './HistoryDrawer.module.css';

interface HistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  liturgyName: string;
  churchId?: number;
  season?: string;
}

type TabType = 'songs' | 'service';
type SeasonViewType = 'bySlot' | 'condensed';

const HistoryDrawer: React.FC<HistoryDrawerProps> = ({
  isOpen,
  onClose,
  liturgyName,
  churchId,
  season: initialSeason
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('songs');
  const [historyData, setHistoryData] = useState<ServiceHistoryResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seasonExpanded, setSeasonExpanded] = useState(false);
  const [seasonViewType, setSeasonViewType] = useState<SeasonViewType>('bySlot');

  const loadHistory = useCallback(async () => {
    if (!liturgyName) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchServiceHistory(liturgyName, churchId, true);
      setHistoryData(data);
    } catch (err) {
      setError('Failed to load history data');
      console.error('Error loading history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [liturgyName, churchId]);

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, loadHistory]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const renderYearCard = (year: HistoryYear) => {
    const selections = activeTab === 'songs' ? year.songSelections : year.serviceMusic;
    const groupedSelections = groupByPlacement(selections);

    return (
      <div
        key={year.liturgicalYear}
        className={`${styles.yearCard} ${year.isCurrent ? styles.currentYear : ''}`}
      >
        <div className={styles.yearCardHeader}>
          <div>
            <span className={styles.yearName}>
              {year.yearLetter ? `Year ${year.yearLetter}` : year.liturgicalYear}
            </span>
            {year.liturgyDate && (
              <span className={styles.yearDate}>{formatDisplayDate(year.liturgyDate)}</span>
            )}
          </div>
          {year.isCurrent && <span className={styles.currentBadge}>Current</span>}
        </div>
        <div className={styles.yearCardBody}>
          {selections.length === 0 ? (
            <div className={styles.songEmpty}>
              {year.isCurrent ? 'Not yet selected' : 'No selections recorded'}
            </div>
          ) : (
            Object.entries(groupedSelections).map(([placement, songs]) => (
              <div key={placement} className={styles.musicSlot}>
                <div className={styles.slotLabel}>{placement}</div>
                <div className={styles.slotContent}>
                  {songs.map((song, idx) => (
                    <div key={idx}>
                      <div className={styles.songTitle}>{song.songTitle}</div>
                      {song.composerName && (
                        <div className={styles.songComposer}>{song.composerName}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  const renderSeasonSection = () => {
    if (!historyData?.seasonHistory) return null;

    const seasonName = historyData.season || initialSeason || 'This Season';

    return (
      <div className={styles.seasonSection}>
        <div
          className={`${styles.seasonHeader} ${seasonExpanded ? styles.expanded : ''}`}
          onClick={() => setSeasonExpanded(!seasonExpanded)}
        >
          <h4>
            <Clock size={16} />
            All {seasonName} Selections
          </h4>
          <ChevronDown size={20} className={styles.chevron} />
        </div>

        {seasonExpanded && (
          <div className={styles.seasonContent}>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewToggleBtn} ${seasonViewType === 'bySlot' ? styles.active : ''}`}
                onClick={() => setSeasonViewType('bySlot')}
              >
                By Slot
              </button>
              <button
                className={`${styles.viewToggleBtn} ${seasonViewType === 'condensed' ? styles.active : ''}`}
                onClick={() => setSeasonViewType('condensed')}
              >
                Condensed
              </button>
            </div>

            {seasonViewType === 'bySlot' ? renderBySlotView() : renderCondensedView()}
          </div>
        )}
      </div>
    );
  };

  const renderBySlotView = () => {
    if (!historyData?.seasonHistory?.bySlot) return null;

    return (
      <div>
        {Object.entries(historyData.seasonHistory.bySlot).map(([slot, selections]) => (
          <div key={slot} className={styles.slotGroup}>
            <div className={styles.slotGroupHeader}>{slot}</div>
            {selections.map((selection, idx) => (
              <div key={idx} className={styles.slotGroupItem}>
                <span className={styles.songName}>{selection.songTitle}</span>
                <span className={styles.yearLabel}>
                  {selection.years.map(y => `Year ${y}`).join(', ')}
                  {selection.count > 1 ? ` · used ${selection.count}×` : ''}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderCondensedView = () => {
    if (!historyData?.seasonHistory?.bySlot) return null;

    // Flatten all selections
    const allSelections: Array<{
      years: string[];
      count: number;
      songTitle: string;
      slot: string;
    }> = [];

    Object.entries(historyData.seasonHistory.bySlot).forEach(([slot, selections]) => {
      selections.forEach(sel => {
        allSelections.push({
          years: sel.years,
          count: sel.count,
          songTitle: sel.songTitle,
          slot: getSlotAbbreviation(slot)
        });
      });
    });

    return (
      <div className={styles.condensedList}>
        {allSelections.map((item, idx) => (
          <div key={idx} className={styles.condensedItem}>
            <span className={styles.yearTag}>
              {item.years.join('/')}{item.count > 1 ? ` ×${item.count}` : ''}
            </span>
            <span className={styles.songName}>{item.songTitle}</span>
            <span className={styles.slotTag}>{item.slot}</span>
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={`${styles.drawer} ${isOpen ? styles.active : ''}`}>
        <div className={styles.drawerHeader}>
          <div>
            <h2>{liturgyName}</h2>
            <div className={styles.subtitle}>Music History</div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.drawerTabs}>
          <button
            className={`${styles.drawerTab} ${activeTab === 'songs' ? styles.active : ''}`}
            onClick={() => setActiveTab('songs')}
          >
            Song Selections
          </button>
          <button
            className={`${styles.drawerTab} ${activeTab === 'service' ? styles.active : ''}`}
            onClick={() => setActiveTab('service')}
          >
            Service Music
          </button>
        </div>

        <div className={styles.drawerBody}>
          {isLoading ? (
            <div className={styles.loading}>Loading history...</div>
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : historyData ? (
            <>
              {historyData.years.map(year => renderYearCard(year))}
              {activeTab === 'songs' && renderSeasonSection()}
            </>
          ) : (
            <div className={styles.empty}>No history data available</div>
          )}
        </div>
      </div>
    </>
  );
};

// Helper functions
function groupByPlacement(selections: HistorySelection[]): Record<string, HistorySelection[]> {
  const grouped = selections.reduce((acc, sel) => {
    // Spelling variants of one slot must land in one group, or the card shows
    // two headings that read the same. See utils/slotNames.
    const key = canonicalSlot(sel.songPlacement);
    if (!acc[key]) acc[key] = [];
    acc[key].push(sel);
    return acc;
  }, {} as Record<string, HistorySelection[]>);

  // Rebuild in the order the service runs.
  const ordered: Record<string, HistorySelection[]> = {};
  Object.keys(grouped)
    .sort((a, b) => slotRank(a) - slotRank(b) || a.localeCompare(b))
    .forEach(key => { ordered[key] = grouped[key]; });
  return ordered;
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getSlotAbbreviation(slot: string): string {
  const abbrevMap: Record<string, string> = {
    'Opening Hymn': 'Open',
    'Processional': 'Proc',
    'Offertory': 'Off',
    'Communion': 'Comm',
    'Closing Hymn': 'Close',
    'Recessional': 'Recess',
    'Anthem': 'Anth',
    'Choir Anthem': 'Anth'
  };
  return abbrevMap[slot] || slot.substring(0, 4);
}

export default HistoryDrawer;
